// Client-side Bugsnag noise filters for browser extensions, known third-party
// scripts, webpack chunk-load timeouts, and spoofed-bot gtag globalThis errors.
// Pure helpers so they can be unit-tested without starting Bugsnag.

const BROWSER_EXTENSION_FILE_PATTERNS = [
  /^safari-extension:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  // PayPal Honey and similar Safari extensions loaded from local .safariextension bundles
  /^file:\/\/\/.*\.safariextension\//i,
]

const IGNORED_THIRD_PARTY_FILE_PATTERNS = [
  /(?:^|[\/.])googletagmanager\.com(?:\/|$)/i,
]

// Webpack async chunk load timeouts, e.g.
// "Loading chunk 1 failed.\n(timeout: https://apco.org.au/packs/js/1-….chunk.js)"
const CHUNK_LOAD_TIMEOUT_MESSAGE_PATTERN = /Loading chunk \d+ failed[\s\S]*\(timeout:/i

// Scanner/bot noise (Bugsnag 6ad56bc189c003161970ee6b): gtag.js ReferenceError where
// the stack file is the HTML page, and the gtag URL is only in the message.
const GLOBALTHIS_UNDEFINED_MESSAGE_PATTERN = /(?:ReferenceError:\s*)?"?globalThis"?\s+is not defined/i
const GTAG_IN_MESSAGE_PATTERN = /(?:googletagmanager\.com|\/gtag\/js)/i

// Spoofed UA on every event of that error: Chrome/83.0.4103.61 on Windows 7.
// Real Chrome 83 defines globalThis; this fingerprint is bot traffic only.
const SPOOFED_CHROME83_WIN7_UA_PATTERN = /Windows NT 6\.1.*Chrome\/83\.0\.4103\.61/i

function stackFrameFiles(event) {
  const files = []
  const errors = (event && event.errors) || []

  for (let i = 0; i < errors.length; i += 1) {
    const stacktrace = errors[i].stacktrace || []
    for (let j = 0; j < stacktrace.length; j += 1) {
      const file = stacktrace[j] && stacktrace[j].file
      if (typeof file === 'string' && file.length > 0) {
        files.push(file)
      }
    }
  }

  return files
}

function eventUserAgent(event) {
  const device = event && event.device
  if (device && typeof device.userAgent === 'string') {
    return device.userAgent
  }
  return ''
}

function isBrowserExtensionFile(file) {
  if (typeof file !== 'string') return false
  return BROWSER_EXTENSION_FILE_PATTERNS.some(function (pattern) {
    return pattern.test(file)
  })
}

function isIgnoredThirdPartyFile(file) {
  if (typeof file !== 'string') return false
  return IGNORED_THIRD_PARTY_FILE_PATTERNS.some(function (pattern) {
    return pattern.test(file)
  })
}

function isNoiseFile(file) {
  return isBrowserExtensionFile(file) || isIgnoredThirdPartyFile(file)
}

function isChunkLoadTimeoutError(error) {
  if (!error || error.errorClass !== 'ChunkLoadError') return false
  const message = error.errorMessage
  if (typeof message !== 'string') return false
  return CHUNK_LOAD_TIMEOUT_MESSAGE_PATTERN.test(message)
}

// Webpack ChunkLoadError timeouts from bots / flaky clients that hit packs/*.chunk.js
function isChunkLoadTimeoutNoise(event) {
  const errors = (event && event.errors) || []
  if (errors.length === 0) return false
  return errors.every(isChunkLoadTimeoutError)
}

function isGlobalThisUndefinedMessage(message) {
  return (
    typeof message === 'string' &&
    GLOBALTHIS_UNDEFINED_MESSAGE_PATTERN.test(message)
  )
}

function isGtagGlobalThisMessage(error) {
  const message = error && error.errorMessage
  if (!isGlobalThisUndefinedMessage(message)) return false
  return GTAG_IN_MESSAGE_PATTERN.test(message)
}

function isSpoofedChrome83Win7Bot(event) {
  return SPOOFED_CHROME83_WIN7_UA_PATTERN.test(eventUserAgent(event))
}

// Drop gtag globalThis ReferenceErrors that the file-based third-party filter
// misses because Bugsnag attributes the stack to the HTML document URL.
function isGtagGlobalThisNoise(event) {
  const errors = (event && event.errors) || []
  if (errors.length === 0) return false

  const allGlobalThis = errors.every(function (error) {
    return isGlobalThisUndefinedMessage(error && error.errorMessage)
  })
  if (!allGlobalThis) return false

  // Message embeds the gtag/GTM script URL (typical for this Bugsnag grouping).
  if (errors.every(isGtagGlobalThisMessage)) return true

  // Stack is only gtag / googletagmanager frames.
  const files = stackFrameFiles(event)
  if (files.length > 0 && files.every(isIgnoredThirdPartyFile)) return true

  // Same spoofed Chrome 83 / Windows 7 UA used by the scanner.
  if (isSpoofedChrome83Win7Bot(event)) return true

  return false
}

// Drop when every reported stack frame is extension / ignored third-party noise,
// when the event is a webpack chunk-load timeout of the bot-noise shape,
// or when it matches the gtag globalThis scanner pattern.
// Events with no file paths (and no other match) are left alone.
function shouldIgnoreBugsnagEvent(event) {
  if (isChunkLoadTimeoutNoise(event)) return true
  if (isGtagGlobalThisNoise(event)) return true

  const files = stackFrameFiles(event)
  if (files.length === 0) return false
  return files.every(isNoiseFile)
}

function bugsnagOnError(event) {
  if (shouldIgnoreBugsnagEvent(event)) {
    return false
  }
}

module.exports = {
  BROWSER_EXTENSION_FILE_PATTERNS,
  IGNORED_THIRD_PARTY_FILE_PATTERNS,
  CHUNK_LOAD_TIMEOUT_MESSAGE_PATTERN,
  GLOBALTHIS_UNDEFINED_MESSAGE_PATTERN,
  GTAG_IN_MESSAGE_PATTERN,
  SPOOFED_CHROME83_WIN7_UA_PATTERN,
  isBrowserExtensionFile,
  isIgnoredThirdPartyFile,
  isChunkLoadTimeoutError,
  isChunkLoadTimeoutNoise,
  isGtagGlobalThisMessage,
  isGtagGlobalThisNoise,
  isSpoofedChrome83Win7Bot,
  shouldIgnoreBugsnagEvent,
  bugsnagOnError,
}
